import {
  Button,
  Container,
  FormField,
  Header,
  Select,
  SpaceBetween,
  Textarea,
} from '@amzn/awsui-components-react-v3';
import { OptionDefinition } from '@amzn/awsui-components-react-v3/polaris/internal/components/option/interfaces';
import { useFormik } from 'formik';
import React, { ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import KeyValue from '../../../../components/KeyValue';
import i18n from '../../../../i18n';
import { ReceiveExceptionStatus } from '../../../../services/receive-exception/types';
import { ResolutionCodeOptions } from '../config';
import { ReceiveExceptionView } from '../types';

export type ResolverFormValues = {
  resolutionNote: string;
  resolutionCode: OptionDefinition;
};

const ResolverFormSchema = yup.object().shape({
  resolutionCode: yup
    .mixed()
    .transform((item) => item && item.value)
    .typeError(i18n.t('Resolution Code is required'))
    .required(i18n.t('Resolution Code is required')),
  resolutionNote: yup.string(),
});

export default function ResolverForm({
  data,
  onSubmit,
}: {
  data: ReceiveExceptionView;
  onSubmit: (values: Record<string, string>) => Promise<void>;
}): ReactElement {
  const { t } = useTranslation();
  const [editResolution, setEditResolution] = useState<boolean>(false);
  const form = useFormik<ResolverFormValues>({
    initialValues: {
      resolutionCode: data.resolutionCodeOption,
      resolutionNote: data.resolutionNote || '',
    },
    validationSchema: ResolverFormSchema,
    onSubmit: async (values) => {
      await onSubmit({
        ...(ResolverFormSchema.cast(values) as Record<string, string>),
        status: ReceiveExceptionStatus.PENDING_ACTION,
      });
      setEditResolution(false);
    },
  });

  const formFields = (
    <SpaceBetween direction="vertical" size="l">
      <FormField
        stretch
        label={t('Resolution Code')}
        errorText={form.touched.resolutionCode && form.errors.resolutionCode}>
        <Select
          data-test-id="select-resolution-code"
          placeholder={t('Select resolution code')}
          options={ResolutionCodeOptions}
          selectedOption={form.values.resolutionCode}
          onChange={(event) => {
            form.setFieldValue('resolutionCode', event.detail.selectedOption);
          }}
        />
      </FormField>
      <FormField
        stretch
        label={t('Resolution Notes')}
        errorText={form.touched.resolutionNote && form.errors.resolutionNote}>
        <Textarea
          data-test-id="input-resolution-note"
          value={form.values.resolutionNote}
          placeholder={t('Write any notes here')}
          onChange={(event) => form.setFieldValue('resolutionNote', event.detail.value)}
        />
      </FormField>
    </SpaceBetween>
  );

  const keyValues = (
    <SpaceBetween direction="vertical" size="l">
      <KeyValue label={t('Resolution Code Provider')}>{data.resolver}</KeyValue>
      <KeyValue label={t('Resolution Code')}>{data.resolutionCode}</KeyValue>
      <KeyValue label={t('Resolution Notes')}>{data.resolutionNote}</KeyValue>
    </SpaceBetween>
  );
  return (
    <>
      {data.status === ReceiveExceptionStatus.ASSIGNED && (
        <Container
          header={
            <Header variant="h2" description="">
              {t('Resolve Exception')}
            </Header>
          }
          footer={
            <Button
              data-test-id="button-submit-resolution"
              onClick={() => form.handleSubmit()}
              variant="primary"
              disabled={form.isSubmitting}>
              {t('Submit Resolution')}
            </Button>
          }>
          {formFields}
        </Container>
      )}
      {(data.status === ReceiveExceptionStatus.RESOLVED ||
        data.status === ReceiveExceptionStatus.CANCELLED) && (
        <Container
          header={
            <Header variant="h2" description="">
              {t('Resolve Exception')}
            </Header>
          }>
          {keyValues}
        </Container>
      )}

      {data.status === ReceiveExceptionStatus.PENDING_ACTION && (
        <Container
          header={
            <Header
              variant="h2"
              description=""
              actions={
                <Button
                  data-test-id="button-edit-resolution"
                  variant="normal"
                  disabled={editResolution}
                  onClick={() => setEditResolution(true)}>
                  {t('Edit Resolution')}
                </Button>
              }>
              {t('Resolve Exception')}
            </Header>
          }
          {...(editResolution && {
            footer: (
              <>
                {editResolution && (
                  <SpaceBetween direction="horizontal" size="m">
                    <Button
                      data-test-id="button-save-resolution"
                      loading={form.isSubmitting}
                      onClick={() => {
                        form.handleSubmit();
                      }}
                      variant="normal">
                      {t('Save')}
                    </Button>
                    <Button
                      data-test-id="button-cancel-edit"
                      onClick={() => {
                        setEditResolution(false);
                      }}
                      variant="normal"
                      disabled={!form.values.resolutionCode}>
                      {t('Cancel')}
                    </Button>
                  </SpaceBetween>
                )}
              </>
            ),
          })}>
          <>
            {editResolution && formFields}
            {!editResolution && keyValues}
          </>
        </Container>
      )}
    </>
  );
}
